/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { jsx } from '@compiled/react';
import { getErrorHash } from '@jira/platform__error-reporting/src/getErrorHash.tsx';
import GenericError from '@jira/illustrations__illustrations/src/ui/adg4/brand/spots/error/components/generic-error/index.tsx';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const errorHeading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.sidebar-boundary.fallback.error-heading',

	defaultMessage: 'Something went wrong',
	description: 'Title for error message when encountering an error with the sidebar navigation',
});

const errorBody = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.sidebar-boundary.fallback.error-body',

	defaultMessage: 'Try refreshing or navigating to another page.',
	description:
		'Description for error message when encountering an error with the sidebar navigation',
});

const errorHashDetails = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.sidebar-boundary.fallback.error-hash-details',

	defaultMessage: 'ERROR: {errorHash}',
	description:
		'Hash details for error message when encountering an error with the sidebar navigation',
});

const styles = cssMap({
	wrapper: {
		backgroundColor: token('color.background.neutral'),
		color: token('color.text.subtle'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		textAlign: 'center',
		alignItems: 'center',
		gap: token('space.100'),
	},
	error: {
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.accent.gray.subtler'),
		color: token('color.text.subtlest'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
});

type FallbackProps = {
	error: Error;
};

export const Fallback = ({ error }: FallbackProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack xcss={styles.wrapper}>
			<GenericError width="40px" alt="" />
			<Heading size="xsmall">{formatMessage(errorHeading)}</Heading>
			<Text>{formatMessage(errorBody)}</Text>
			<Box xcss={styles.error}>
				{formatMessage(errorHashDetails, { errorHash: getErrorHash(error) })}
			</Box>
		</Stack>
	);
};
